import React, { useState, useEffect } from 'react';
import { faqsAPI } from '../services/api';
import { HelpCircle, ChevronDown, ChevronUp, Stethoscope, Shield, Heart } from 'lucide-react';

const categoryIcons = { Treatment: Stethoscope, Prevention: Shield, Consultation: HelpCircle, Support: Heart };

const PatientResources = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    faqsAPI.getAll().then(data => { setFaqs(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(faqs.map(f => f.category))];
  const filtered = activeCategory === 'All' ? faqs : faqs.filter(f => f.category === activeCategory);

  return (
    <section id="patient-resources" className="py-20 px-4 bg-gradient-to-b from-white via-blue-50/30 to-white" data-testid="patient-resources-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
            <HelpCircle className="w-5 h-5" />
            <span>Patient Resources</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Frequently Asked <span className="text-blue-600">Questions</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Common questions about cancer treatment, screening, and support
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
              }`}
              data-testid={`faq-category-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="space-y-3">
            {filtered.map(faq => {
              const isOpen = openId === faq.id;
              const Icon = categoryIcons[faq.category] || HelpCircle;
              return (
                <div
                  key={faq.id}
                  className={`bg-white rounded-xl border transition-all ${isOpen ? 'border-blue-300 shadow-lg' : 'border-gray-200 hover:border-blue-200'}`}
                  data-testid={`faq-item-${faq.id}`}
                >
                  <button
                    onClick={() => setOpenId(isOpen ? null : faq.id)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="font-semibold text-gray-900 text-sm md:text-base">{faq.question}</span>
                    </div>
                    {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pl-14">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PatientResources;
